import { parseValue } from '@root/theme/themeUtils';
import { tokens } from '@root/theme/tokens';
import React from 'react';
import { View, ViewProps, ViewStyle } from 'react-native';

// Box component that mimics NativeBase Box behavior
// Maps common NativeBase props to React Native View styles
interface BoxProps extends ViewProps {
  // Layout props
  flex?: number;
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';

  // Spacing props (convert to numbers)
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string; // paddingHorizontal
  paddingY?: number | string; // paddingVertical
  marginX?: number | string; // marginHorizontal
  marginY?: number | string; // marginVertical

  // Background
  bg?: string; // backgroundColor
  backgroundColor?: string;

  // Border
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | number;
  borderRadius?: number;
  borderWidth?: number | string;
  borderColor?: string;

  // Dimensions
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;

  // NativeBase shortcuts
  w?: number | string;
  h?: number | string;
  px?: number | string;
  py?: number | string;
  mx?: number | string;
  my?: number | string;
  mt?: number | string;
  mb?: number | string;
  ml?: number | string;
  mr?: number | string;
  pt?: number | string;
  pb?: number | string;
  pl?: number | string;
  pr?: number | string;

  // NativeBase theme variants (we'll ignore these for now)
  _dark?: any;
  _light?: any;
  _hover?: any;
  _focus?: any;
  _pressed?: any;
  _disabled?: any;
}

const Box: React.FC<BoxProps> = ({
  flex,
  flexDirection,
  justifyContent,
  alignItems,
  alignSelf,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingX,
  paddingY,
  marginX,
  marginY,
  bg,
  backgroundColor,
  rounded,
  borderRadius,
  borderWidth,
  borderColor,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  // NativeBase shortcuts
  w,
  h,
  px,
  py,
  mx,
  my,
  mt,
  mb,
  ml,
  mr,
  pt,
  pb,
  pl,
  pr,
  style,
  children,
  // Ignore theme variants
  _dark,
  _light,
  _hover,
  _focus,
  _pressed,
  _disabled,
  ...rest
}) => {
  // Use parseValue directly from themeUtils

  // Handle rounded prop
  const getBorderRadius = (): number | undefined => {
    if (borderRadius !== undefined) {
      return parseValue(borderRadius);
    }

    if (rounded !== undefined) {
      if (typeof rounded === 'number') {
        return rounded;
      }

      // Map rounded string values to theme values
      switch (rounded) {
        case 'sm':
          return tokens.borderRadius.sm;
        case 'md':
          return tokens.borderRadius.md;
        case 'lg':
          return tokens.borderRadius.lg;
        case 'xl':
          return tokens.borderRadius.xl;
        case 'full':
          return tokens.borderRadius.full;
        default:
          return undefined;
      }
    }

    return undefined;
  };

  // Create style object with only defined values
  const boxStyle: ViewStyle = {};

  // Layout
  if (flex !== undefined) boxStyle.flex = flex;
  if (flexDirection !== undefined) boxStyle.flexDirection = flexDirection;
  if (justifyContent !== undefined) boxStyle.justifyContent = justifyContent;
  if (alignItems !== undefined) boxStyle.alignItems = alignItems;
  if (alignSelf !== undefined) boxStyle.alignSelf = alignSelf;

  // Spacing - use shortcuts if provided, otherwise use full names
  const marginTopValue = parseValue(mt || marginTop);
  const marginBottomValue = parseValue(mb || marginBottom);
  const marginLeftValue = parseValue(ml || marginLeft);
  const marginRightValue = parseValue(mr || marginRight);
  const marginXValue = parseValue(mx || marginX);
  const marginYValue = parseValue(my || marginY);
  const paddingTopValue = parseValue(pt || paddingTop);
  const paddingBottomValue = parseValue(pb || paddingBottom);
  const paddingLeftValue = parseValue(pl || paddingLeft);
  const paddingRightValue = parseValue(pr || paddingRight);
  const paddingXValue = parseValue(px || paddingX);
  const paddingYValue = parseValue(py || paddingY);

  if (marginTopValue !== undefined) boxStyle.marginTop = marginTopValue;
  if (marginBottomValue !== undefined) boxStyle.marginBottom = marginBottomValue;
  if (marginLeftValue !== undefined) boxStyle.marginLeft = marginLeftValue;
  if (marginRightValue !== undefined) boxStyle.marginRight = marginRightValue;
  if (marginXValue !== undefined) boxStyle.marginHorizontal = marginXValue;
  if (marginYValue !== undefined) boxStyle.marginVertical = marginYValue;
  if (paddingTopValue !== undefined) boxStyle.paddingTop = paddingTopValue;
  if (paddingBottomValue !== undefined) boxStyle.paddingBottom = paddingBottomValue;
  if (paddingLeftValue !== undefined) boxStyle.paddingLeft = paddingLeftValue;
  if (paddingRightValue !== undefined) boxStyle.paddingRight = paddingRightValue;
  if (paddingXValue !== undefined) boxStyle.paddingHorizontal = paddingXValue;
  if (paddingYValue !== undefined) boxStyle.paddingVertical = paddingYValue;

  // Background
  if (bg !== undefined) boxStyle.backgroundColor = bg;
  else if (backgroundColor !== undefined) boxStyle.backgroundColor = backgroundColor;

  // Border
  const borderRadiusValue = getBorderRadius();
  const borderWidthValue = parseValue(borderWidth);
  if (borderRadiusValue !== undefined) boxStyle.borderRadius = borderRadiusValue;
  if (borderWidthValue !== undefined) boxStyle.borderWidth = borderWidthValue;
  if (borderColor !== undefined) boxStyle.borderColor = borderColor;

  // Dimensions - use shortcuts if provided, otherwise use full names
  const widthValue = parseValue(w || width);
  const heightValue = parseValue(h || height);
  const minWidthValue = parseValue(minWidth);
  const minHeightValue = parseValue(minHeight);
  const maxWidthValue = parseValue(maxWidth);
  const maxHeightValue = parseValue(maxHeight);

  if (widthValue !== undefined) boxStyle.width = widthValue;
  if (heightValue !== undefined) boxStyle.height = heightValue;
  if (minWidthValue !== undefined) boxStyle.minWidth = minWidthValue;
  if (minHeightValue !== undefined) boxStyle.minHeight = minHeightValue;
  if (maxWidthValue !== undefined) boxStyle.maxWidth = maxWidthValue;
  if (maxHeightValue !== undefined) boxStyle.maxHeight = maxHeightValue;

  // Extract position-related props from rest and add to style
  const { position, top, right, bottom, left, zIndex, ...remainingRest } = rest as any;
  if (position || top || right || bottom || left || zIndex) {
    Object.assign(boxStyle, {
      position,
      top,
      right,
      bottom,
      left,
      zIndex,
    });
  }

  return (
    <View style={[boxStyle, style]} {...remainingRest}>
      {children}
    </View>
  );
};

export default Box;
